import { resolveApiPath } from "@rapid/utils/api/utils";
import pluralize from "pluralize";

/**
 * Uses ChatGPT to extract object names from a natural language prompt
 */
export async function extractObjectsFromPromptWithAI(
    prompt: string,
    abortController?: AbortController
): Promise<string[]> {
    try {
        const url = resolveApiPath("/api/extract-objects");

        const response = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ prompt }),
            signal: abortController?.signal,
            credentials: "include"
        });

        if (!response.ok) {
            const errorText = await response.text();
            let errorMessage = "Object extraction failed";
            try {
                const errorJson = JSON.parse(errorText);
                errorMessage = errorJson.error || errorJson.message || errorMessage;
            } catch {
                errorMessage = errorText || errorMessage;
            }
            throw new Error(errorMessage);
        }

        const result = await response.json();
        return result.objects || [];
    } catch (error) {
        console.error("Failed to extract objects with AI:", error);
        throw error;
    }
}

/**
 * Detects if the input is a natural language prompt instead of a comma-separated list
 */
export function isNaturalLanguagePrompt(input: string): boolean {
    const trimmed = input.trim();

    // Check for common prompt patterns
    const promptPatterns = [
        /^(find|detect|locate|identify|search for|look for|looking for|i want to|i need to|show me|get me|give me)/i,
        /\b(in (the|my|this)|from (the|my|this)|on (the|my|this))\b/i,
        /\b(images?|photos?|pictures?|videos?|files?)\b/i,
        /(that|which|where|who|what|when)\b/i,
        /\b(are|is|am|was|were|be|been)\b/i
    ];

    // If matches any prompt pattern, it's likely a natural language prompt
    if (promptPatterns.some((pattern) => pattern.test(trimmed))) {
        return true;
    }

    // If it's more than 5 words, likely a sentence
    const wordCount = trimmed.split(/\s+/).length;
    if (wordCount > 5) {
        return true;
    }

    return false;
}

export type ProcessedPromptResult = {
    processedInput: string;
    wasExtracted: boolean;
    originalInput?: string;
    extractedObjects?: string[];
};

/**
 * Singularizes a list of object names
 */
function singularizeObjects(objects: string[]): string[] {
    return objects.map((obj) => pluralize.singular(obj.trim())).filter((obj) => obj.length > 0);
}

/**
 * Processes user input using AI: if it's a natural language prompt, uses ChatGPT to extract objects;
 * otherwise returns the original comma-separated input. Always singularizes object names.
 */
export async function processTextPromptInputWithAI(
    input: string,
    abortController?: AbortController
): Promise<ProcessedPromptResult> {
    const trimmed = input.trim();

    if (!trimmed) {
        return { processedInput: "", wasExtracted: false };
    }

    if (isNaturalLanguagePrompt(trimmed)) {
        const objects = await extractObjectsFromPromptWithAI(trimmed, abortController);
        const singularized = singularizeObjects(objects);
        return {
            processedInput: singularized.join(", "),
            wasExtracted: true,
            originalInput: trimmed,
            extractedObjects: singularized
        };
    }

    // Parse comma-separated list and singularize each object
    const objects = trimmed.split(",").map((obj) => obj.trim());
    const singularized = singularizeObjects(objects);
    return { processedInput: singularized.join(", "), wasExtracted: false };
}
